import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type {
  VerificationEmailConfigDTO,
  VerificationEmailConfigUpdateParams,
  VerificationEmailProvider,
} from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid verification email config': '验证码邮件配置无效，请检查当前启用通道的必填项',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

type VerificationEmailForm = VerificationEmailConfigUpdateParams;

const EMPTY_FORM: VerificationEmailForm = {
  provider: 'disabled',
  aliyun: {
    access_key_id: '',
    access_key_secret: '',
    region: '',
    account_name: '',
    from_alias: '',
    tag_name: '',
    reply_to_address: false,
  },
  tencent: {
    secret_id: '',
    secret_key: '',
    region: '',
    from_email_address: '',
    reply_to_addresses: '',
    subject: '',
    template_id: 0,
    trigger_type: 1,
    template_code_key: 'code',
  },
};

function dtoToForm(dto: VerificationEmailConfigDTO): VerificationEmailForm {
  return {
    provider: dto.provider,
    aliyun: {
      access_key_id: dto.aliyun.access_key_id,
      access_key_secret: dto.aliyun.access_key_secret,
      region: dto.aliyun.region,
      account_name: dto.aliyun.account_name,
      from_alias: dto.aliyun.from_alias,
      tag_name: dto.aliyun.tag_name,
      reply_to_address: dto.aliyun.reply_to_address,
    },
    tencent: {
      secret_id: dto.tencent.secret_id,
      secret_key: dto.tencent.secret_key,
      region: dto.tencent.region,
      from_email_address: dto.tencent.from_email_address,
      reply_to_addresses: dto.tencent.reply_to_addresses,
      subject: dto.tencent.subject,
      template_id: dto.tencent.template_id,
      trigger_type: dto.tencent.trigger_type,
      template_code_key: dto.tencent.template_code_key,
    },
  };
}

export function useVerificationEmailConfig() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<VerificationEmailConfigDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingProvider, setDeletingProvider] = useState<'aliyun' | 'tencent' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<VerificationEmailForm>(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getVerificationEmailConfig();
      setData(result);
      setForm(dtoToForm(result));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setProvider = useCallback((provider: VerificationEmailProvider) => {
    setForm((prev) => ({ ...prev, provider }));
  }, []);

  const updateAliyunField = useCallback(
    <K extends keyof VerificationEmailForm['aliyun']>(key: K, value: VerificationEmailForm['aliyun'][K]) => {
      setForm((prev) => ({ ...prev, aliyun: { ...prev.aliyun, [key]: value } }));
    },
    [],
  );

  const updateTencentField = useCallback(
    <K extends keyof VerificationEmailForm['tencent']>(key: K, value: VerificationEmailForm['tencent'][K]) => {
      setForm((prev) => ({ ...prev, tencent: { ...prev.tencent, [key]: value } }));
    },
    [],
  );

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const result = await settingsService.updateVerificationEmailConfig(form);
      setData(result);
      setForm(dtoToForm(result));
      addToast('success', '验证码邮件配置已保存，并将在下一次发码时立即生效');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSaving(false);
    }
  }, [addToast, form]);

  const removeProvider = useCallback(async (provider: 'aliyun' | 'tencent') => {
    setDeletingProvider(provider);
    try {
      const result = await settingsService.deleteVerificationEmailProvider(provider);
      setData(result);
      setForm(dtoToForm(result));
      addToast('success', `${provider === 'aliyun' ? '阿里云' : '腾讯云'}配置已删除`);
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setDeletingProvider(null);
    }
  }, [addToast]);

  return {
    data,
    form,
    loading,
    saving,
    deletingProvider,
    error,
    setProvider,
    updateAliyunField,
    updateTencentField,
    save,
    removeProvider,
    reload: load,
  };
}
